import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseControl } from '../base.control';
import { SeahorseTemplateDirective } from '@seahorse/common';

type OptionValueFn<T> = (item: T) => any;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sh-select-control',
  templateUrl: './select.control.html',
  styleUrls: ['./select.control.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectControl),
      multi: true,
    },
    {
      provide: BaseControl,
      useExisting: forwardRef(() => SelectControl),
    },
  ],
  standalone: false,
})
export class SelectControl<T> extends BaseControl<T | null> {
  private _options: T[] = [];

  @Input()
  set options(v: T[] | null | undefined) {
    this._options = v ?? [];
  }
  get options(): T[] {
    return this._options;
  }

  @Input() optionLabel?: string;
  @Input() optionValue?: string | OptionValueFn<T>;
  @Input() filter = true;
  @Input() filterBy?: string;
  @Input() id?: string;
  @Input() class?: string;
  @Input() appendTo = 'self';
  @Input() editable = false;

  @Output() valueChange = new EventEmitter<T | null>();
  @Output() filterChange = new EventEmitter<string>();

  @ContentChildren(SeahorseTemplateDirective, { descendants: true })
  templates?: QueryList<SeahorseTemplateDirective>;

  get itemTemplate() {
    return this.templates?.find((x) => x.type === 'item')?.templateRef;
  }

  get selectedItemTemplate() {
    return this.templates?.find((x) => x.type === 'selectedItem')?.templateRef;
  }

  get primeOptionValue(): string {
    return typeof this.optionValue === 'string' ? this.optionValue : '';
  }

  get inputValue(): any {
    if (typeof this.optionValue === 'function') {
      return this.findOptionByValue(this.value);
    }

    return this.value;
  }

  set inputValue(raw: any) {
    if (this.optionValue && typeof this.optionValue === 'function') {
      const computed = raw
        ? (this.optionValue as OptionValueFn<T>)(raw as T)
        : null;
      this.value = computed;
      this.onChangeCb?.(computed);
      this.valueChange.emit(computed);
      return;
    }

    if (this.optionValue) {
      this.value = raw;
      this.onChangeCb?.(raw);
      this.valueChange.emit(raw);
      return;
    }

    this.value = raw as T;
    this.onChangeCb?.(raw as T);
    this.valueChange.emit(raw as T);
  }

  markTouched() {
    this.onTouchedCb?.();
    this.control?.markAsTouched();
    this.control?.updateValueAndValidity();
  }

  emitFilter(evt: any) {
    const value = evt?.filter ?? evt?.query ?? evt?.value ?? '';
    this.filterChange.emit(String(value));
  }

  private findOptionByValue(value: any): T | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (!this.options?.length) {
      return null;
    }

    const fn = this.optionValue as OptionValueFn<T>;
    return this.options.find((o) => fn(o) === value) ?? null;
  }
}
